import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API } from "@gui/infrastructure/pass-through/api";
import { UserContext } from "@gui/contexts/user/UserContext";
import { ALL_ADMIN_LINKS, DEVELOPER_LINKS, USER_LINKS } from "@gui/widgets/Toolbar";
import { issueCountsFromIds, issueQueuesByPath, issueQueuesToRequest } from "./issueCountQueues";

export interface IssueCountsValue {
	/** Current cached counts keyed by route path. `undefined` means "not yet fetched / not
	 *  visible to this user". A value of `0` means "fetched, no issues". */
	counts: Record<string, number>;
	/** True while the initial count fetch is in flight. False once it has settled, answered or not. */
	loading: boolean;
	/** Live-update a count from a page that just fetched its own list. */
	setCount: (path: string, count: number) => void;
}

/**
 * Owns issue-count state and the count fetch: one request, once on mount, for the job ids on every
 * issues queue behind a badge the current user can see (`issueCountQueues` holds which queues those
 * are and how ids become counts). Live updates come via `setCount` from page-level vms after their
 * own fetch.
 *
 * The request is issued as soon as the user is known and is never deferred: the badges show a spinner
 * until it settles, and every badge appears together when it does.
 *
 * Extracted out of `IssueCountsContext.tsx` so the Context provider can stay
 * a presentational shell — all API calls + effects live here.
 */
export function useIssueCountsState(): IssueCountsValue {
	const user = useContext(UserContext);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [loading, setLoading] = useState(false);
	const startedRef = useRef(false);

	const visiblePaths = useMemo(() => {
		const paths = new Set(USER_LINKS.map((l) => l.to));
		if (user?.isAdmin) ALL_ADMIN_LINKS.forEach((l) => paths.add(l.to));
		if (user?.isDeveloper) DEVELOPER_LINKS.forEach((l) => paths.add(l.to));
		return paths;
	}, [user?.isAdmin, user?.isDeveloper]);

	useEffect(() => {
		if (!user) return;
		// Dedup against StrictMode double-invoke; the cache lives for the page lifetime.
		if (startedRef.current) return;
		const queuesByPath = issueQueuesByPath(visiblePaths, user.isDeveloper ?? false);
		const queues = issueQueuesToRequest(queuesByPath);
		if (queues.length === 0) return;
		startedRef.current = true;
		setLoading(true);
		API.Data.JobIssueIds.get(queues)
			// A request that fails outright leaves every badge without a count; a queue the server could
			// not read leaves only the badges that count it (see issueCountsFromIds).
			.then((answer) => issueCountsFromIds(queuesByPath, answer))
			.catch((): Record<string, number> => ({}))
			.then((next) => {
				setCounts((prev) => ({ ...prev, ...next }));
				setLoading(false);
			});
	}, [user, visiblePaths]);

	const setCount = useCallback((path: string, count: number) => {
		setCounts((prev) => (prev[path] === count ? prev : { ...prev, [path]: count }));
	}, []);

	return useMemo(() => ({ counts, loading, setCount }), [counts, loading, setCount]);
}
